import { ButtonLink } from "./button";
import "./marketing-cta.css";

/**
 * The shared closing CTA band (the approved pricing-page design): a teal
 * gradient panel with a faint street-map backdrop, two dropped pins, and a
 * centered title / lede / pill button / note. Extracted from PricingView so the
 * landing and every other marketing surface use ONE component instead of
 * drifting copies. Copy is passed in; `ctaHref` can be a route or an in-page
 * anchor (e.g. "#make-a-map").
 */

function PinIcon({ fill, size = 14, rim }: { fill: string; size?: number; rim?: string }) {
  return (
    <svg width={size} height={size * 1.29} viewBox="0 0 34 44" fill="none" aria-hidden style={{ display: "block" }}>
      <path
        d="M17 42.5C17 42.5 3.5 24.8 3.5 15.2C3.5 7.7 9.5 1.8 17 1.8C24.5 1.8 30.5 7.7 30.5 15.2C30.5 24.8 17 42.5 17 42.5Z"
        style={{ fill, stroke: rim }}
        strokeWidth={rim ? 1.6 : 0}
      />
      <circle cx="17" cy="15.2" r="5" style={{ fill: "var(--color-accent-on-accent)" }} />
    </svg>
  );
}

/** Faint stylized street map for the CTA band: parcels, roads, a dashed route, place dots. */
function CtaMap() {
  return (
    <svg viewBox="0 0 1140 360" preserveAspectRatio="xMidYMid slice" aria-hidden className="mcta__map">
      <g fill="currentColor" fillOpacity="0.045">
        <path d="M138 188 L272 172 L288 250 L152 268 Z" />
        <path d="M348 44 L462 34 L474 104 L358 116 Z" />
        <path d="M676 292 L818 280 L830 350 L688 364 Z" />
        <path d="M886 168 L996 156 L1008 222 L896 236 Z" />
        <path d="M60 20 L168 12 L178 78 L68 88 Z" />
      </g>
      <g fill="none" stroke="currentColor" strokeLinecap="round">
        <path d="M-20 148 C 240 126, 520 174, 780 148 S 1080 116, 1170 130" strokeOpacity="0.12" strokeWidth="5" />
        <path d="M322 -20 C 302 100, 350 240, 318 380" strokeOpacity="0.1" strokeWidth="3" />
        <path d="M842 -20 C 868 120, 814 250, 854 380" strokeOpacity="0.1" strokeWidth="3" />
        <path d="M-20 246 C 220 232, 470 262, 720 244 S 1060 224, 1170 238" strokeOpacity="0.07" strokeWidth="1.5" />
        <path d="M-20 58 C 200 72, 430 44, 660 60 S 1010 80, 1170 58" strokeOpacity="0.07" strokeWidth="1.5" />
        <path d="M562 -20 C 582 90, 542 220, 574 380" strokeOpacity="0.06" strokeWidth="1.5" />
        <path d="M118 -20 C 104 120, 122 260, 98 380" strokeOpacity="0.06" strokeWidth="1.5" />
      </g>
      <path
        d="M 106 132 C 250 290, 520 326, 730 282 S 980 190, 1016 164"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.3"
        strokeWidth="2.5"
        strokeDasharray="2 9"
        strokeLinecap="round"
      />
      <circle cx="320" cy="148" r="4" fill="currentColor" fillOpacity="0.45" />
      <circle cx="562" cy="248" r="3" fill="currentColor" fillOpacity="0.3" />
      <circle cx="852" cy="146" r="4" fill="currentColor" fillOpacity="0.4" />
      <circle cx="318" cy="60" r="3" fill="currentColor" fillOpacity="0.3" />
      <circle cx="843" cy="238" r="4" fill="currentColor" fillOpacity="0.35" />
    </svg>
  );
}

export interface MarketingCtaProps {
  title: string;
  lede: string;
  ctaLabel: string;
  /** Route ("/") or in-page anchor ("#make-a-map"). */
  ctaHref: string;
  note?: string;
}

export function MarketingCta({ title, lede, ctaLabel, ctaHref, note }: MarketingCtaProps) {
  return (
    <section className="mcta-sec">
      <div className="mcta">
        <CtaMap />
        <div aria-hidden className="mcta__pin mcta__pin--left mcta-drop mcta-drop--late">
          <PinIcon fill="var(--color-accent-on-accent)" size={28} />
        </div>
        <div aria-hidden className="mcta__pin mcta__pin--right">
          <div className="mcta-drop mcta-drop--later">
            <div className="mcta-float">
              <PinIcon fill="var(--color-secondary-default)" rim="var(--color-accent-on-accent)" size={42} />
            </div>
          </div>
        </div>
        <div className="mcta__inner">
          <h2 className="mcta__title">{title}</h2>
          <p className="mcta__lede">{lede}</p>
          <ButtonLink href={ctaHref} variant="inverse" size="xl" className="mcta__btn">
            {ctaLabel}
          </ButtonLink>
          {note ? <div className="mcta__note">{note}</div> : null}
        </div>
      </div>
    </section>
  );
}
